import CollaborationStore from '@/store/collaboration.store';
import Chapter from './Chapter';
import ArrowIcon from '@/assets/common/ic_arrow_double_down.svg?react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash';
import Tooltip from '@/component/Tooltip';
import lottieData from '@/assets/generating.json';
import lottie from 'lottie-web';
import ImagePreviewer, { ImagePreviewerRef } from '@/component/ImagePreviewer';

const ChapterSummary = () => {
  const chapterSummary = CollaborationStore((state) => state.chapterSummary);
  const meetingFinish = CollaborationStore((state) => state.meetingFinish);
  const showChapterDetail = CollaborationStore((state) => state.showChapterDetail);
  const setShowChapterDetail = CollaborationStore((state) => state.setChapterDetail);
  const keyframeArr = CollaborationStore((state) => state.keyframeArr);
  const [scrolling, setScrolling] = useState(false);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  const scrollRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<HTMLDivElement>(null);
  const imagePreviewerRef = useRef<ImagePreviewerRef>(null);

  const showGoBackToBottom = showScrollBtn && !scrolling;

  const handleShowChapterDetail = () => {
    setShowChapterDetail(!showChapterDetail);
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.onscroll = throttle(
        () => {
          !scrolling && setScrolling(true);
          autoScroll && setAutoScroll(false);
        },
        200,
        {
          trailing: true,
          leading: true,
        },
      );

      containerRef.current.onscrollend = () => {
        setScrolling(false);
      };
    }
  }, [containerRef.current]);

  useEffect(() => {
    if (autoScroll) {
      scrollRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [chapterSummary.length, autoScroll]);
  useEffect(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'instant',
    });
  }, [showChapterDetail]);

  useEffect(() => {
    if (lottieRef.current) {
      lottie.destroy();
      const instance = lottie.loadAnimation({
        container: lottieRef.current,
        loop: true,
        autoplay: true,
        renderer: 'svg',
        animationData: lottieData,
      });
      instance.setSpeed(1.7);
    }
    return () => {
      lottie.destroy();
    };
  }, [lottieRef.current]);

  const handlePreview = (keyframeIndex?: number) => {
    const images = chapterKeyframesArr.flat();
    const imageUrls = images.map(item=>item.keyFrameUrl);
    imagePreviewerRef.current?.handlePreview(imageUrls, keyframeIndex)
  }

  const chapterKeyframesArr = useMemo(
    () =>{
      if(chapterSummary && keyframeArr){
        let keyframeIndex = 0;
        const formatKeyframeArr = keyframeArr.map(item=>{
          const chapterIndex = chapterSummary.findIndex(chapter=> (item.beginTimestamp >= chapter.starttime && item.beginTimestamp <= chapter.endtime) || (item.endTimestamp >= chapter.starttime && item.endTimestamp <= chapter.endtime));
          item.chapterIndex = chapterIndex;
          return item;
        });
        return chapterSummary.map((_chapter, chapterIndex) => {
          return formatKeyframeArr.filter(item=> {
            if(item.chapterIndex === chapterIndex){
              item.keyframeIndex = keyframeIndex;
              keyframeIndex++;
              return true;
            }
            return false;
          });
        });
      }
      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [chapterSummary.length, keyframeArr.length]
  );

  return (
    <div className="flex flex-col gap-2.5 m-4 overflow-auto" ref={containerRef}>
      {chapterSummary.map((chapter, index, arr) => (
        <Chapter
          key={index}
          chapter={chapter}
          index={index}
          onVisibleChange={
            index === arr.length - 1
              ? (v) => {
                  console.log('ChapterSummary  v:', v);
                  setShowScrollBtn(!v);
                }
              : undefined
          }
          handlePreview={handlePreview}
          chapterKeyframes={chapterKeyframesArr[index] || []}
        />
      ))}
      {chapterSummary.length > 0 && (
        <div onClick={handleShowChapterDetail} className="text-mx-primary text-3 ml-5 font-500 cursor-pointer">
          {showChapterDetail ? '收起' : '展开详情'}
        </div>
      )}
      {!meetingFinish && (
        <div className="flex flex-row items-center mt-5.5">
          <div className="size-4.5 mr-1" ref={lottieRef} />
          <div className="text-mx-quaternary text-3 font-400 lh-5.5">正在生成中</div>
        </div>
      )}
      {showGoBackToBottom && (
        <Tooltip content={<div className="text-3.5 lh-5.5 my-1">回到当前</div>}>
          <div
            className="rounded-full size-10 bg-white absolute right-4 bottom-4 flex items-center justify-center cursor-pointer"
            onClick={() => {
              setAutoScroll(true);
              scrollRef.current?.scrollIntoView({
                behavior: 'smooth',
              });
            }}
          >
            <ArrowIcon className="size-4.5" />
          </div>
        </Tooltip>
      )}
      <div ref={scrollRef}></div>
      <ImagePreviewer ref={imagePreviewerRef} />
    </div>
  );
};

export default ChapterSummary;
